/**
 * Canvas 2D graph renderer.
 *
 * Batching is the baseline architecture here, not an optimization: 8000
 * individual stroke() calls is roughly 15ms, three batched strokes is roughly
 * 2ms. Scratch buffers are sized once per scene so a frame allocates nothing.
 */
import { toScreenX, toScreenY, type Camera } from "./camera";
import { LABEL_ALPHA_FLOOR, LABEL_PX, MAX_LABELS, labelOpacity, truncateLabel } from "./labels";
import { MAX_R_PX, MIN_R_PX, nodeRadius, radiusZoom } from "./radius";
import type { GraphPalette } from "./palette";
import type { GraphData, GraphNode } from "./types";

/** Nodes just off-screen still matter: their links cross the viewport. */
const CULL_MARGIN = 64;
const ARROW_MIN_Z = 0.6;
const ARROW_LEN = 7;
const ARROW_WIDE = 3.6;
/** Below this everything collapses to flat colour with no highlight buckets. */
const FLAT_Z = 0.35;
const MAX_DRAWN_LINKS = 12000;
const TAU = Math.PI * 2;

/** Bucket ids for link styling. */
const L_DIM = 0;
const L_NORMAL = 1;
const L_HIGH = 2;

export interface RenderDisplay {
  showArrows: boolean;
  colorByFolder: boolean;
  textFadeMultiplier: number;
  nodeSizeMultiplier: number;
  lineSizeMultiplier: number;
}

export interface RenderScene {
  data: GraphData;
  camera: Camera;
  /** Link endpoint indices, precomputed once per topology. */
  linkA: Int32Array;
  linkB: Int32Array;
  /** Smoothed positions, interleaved x,y. */
  disp: Float32Array;
  /** Entrance progress 0..1 per node; 1 when not animating. */
  enter: Float32Array;
  /** Progressive degree, so hubs visibly grow during playback. */
  degree: Int32Array;
  activeNodes: number;
  activeLinks: number;
  /** -1 when nothing is hovered. */
  hoverIndex: number;
  /**
   * 0..1 eased hover emphasis. The controller ramps this over ~100ms so the
   * dim washes in and out instead of snapping, which is the Obsidian feel.
   * On hover end it keeps the last highlight set while this decays.
   */
  dimStrength: number;
  /** 1 for the hovered node and its immediate neighbours. */
  highlight: Uint8Array;
  /** Index of the currently open note, or -1. */
  currentIndex: number;
  palette: GraphPalette;
  display: RenderDisplay;
}

export interface RenderStats {
  drawnNodes: number;
  drawnLinks: number;
  drawnLabels: number;
}

function easeOutCubic(t: number): number {
  const u = 1 - t;
  return 1 - u * u * u;
}

/** Overshoot, so a node arriving actually pops rather than sliding in. */
function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  const u = t - 1;
  return 1 + c3 * u * u * u + c1 * u * u;
}

export class GraphRenderer {
  private sx = new Float32Array(0);
  private sy = new Float32Array(0);
  private r = new Float32Array(0);
  private visible = new Uint8Array(0);
  /** Small integer colour id per node, so bucketing needs no string work. */
  private colorId = new Uint8Array(0);
  private colors: string[] = [];
  private buckets: Int32Array[] = [];
  private bucketLen: number[] = [];
  private labelOrder = new Int32Array(0);
  private widthCache = new Map<string, number>();
  private sceneKey = "";
  /** Frame-time EMA driving the adaptive escape valve. */
  private frameEma = 12;
  private lastStats: RenderStats = { drawnNodes: 0, drawnLinks: 0, drawnLabels: 0 };

  get stats(): RenderStats {
    return this.lastStats;
  }

  /**
   * Recompute per-node colour ids and the degree-descending label order.
   * Cheap, but not per-frame work, so it is keyed off a scene fingerprint.
   */
  private ensureScene(scene: RenderScene): void {
    const { data, palette } = scene;
    const byFolder = scene.display.colorByFolder;
    const n = data.nodes.length;
    const key = `${n}|${data.links.length}|${palette.node}|${byFolder ? 1 : 0}|${data.nodes
      .map((x) => x.groupColor ?? (byFolder ? x.autoColor : "") ?? "")
      .join(",")}`;
    if (key === this.sceneKey && this.sx.length === n) return;
    this.sceneKey = key;

    this.sx = new Float32Array(n);
    this.sy = new Float32Array(n);
    this.r = new Float32Array(n);
    this.visible = new Uint8Array(n);
    this.colorId = new Uint8Array(n);
    this.widthCache.clear();

    const kindColor: Record<GraphNode["kind"], string> = {
      note: palette.node,
      unresolved: palette.phantom,
      tag: palette.tag,
      attachment: palette.attachment,
    };
    const ids = new Map<string, number>();
    this.colors = [];
    for (let i = 0; i < n; i++) {
      const node = data.nodes[i]!;
      // precedence: explicit group > folder hue (when enabled) > kind default
      const color = node.groupColor ?? (byFolder ? node.autoColor : undefined) ?? kindColor[node.kind];
      let id = ids.get(color);
      if (id === undefined) {
        id = this.colors.length;
        ids.set(color, id);
        this.colors.push(color);
      }
      this.colorId[i] = id;
    }
    this.buckets = this.colors.map(() => new Int32Array(n));
    this.bucketLen = this.colors.map(() => 0);

    this.labelOrder = new Int32Array(n);
    for (let i = 0; i < n; i++) this.labelOrder[i] = i;
    const deg = data.nodes.map((node) => node.degree);
    // hubs get labels first: that single rule is why a large graph stays
    // readable instead of becoming a text smear
    this.labelOrder.sort((a, b) => (deg[b] ?? 0) - (deg[a] ?? 0));
  }

  draw(ctx: CanvasRenderingContext2D, w: number, h: number, scene: RenderScene): void {
    const t0 = performance.now();
    this.ensureScene(scene);
    ctx.clearRect(0, 0, w, h);
    this.lastStats = { drawnNodes: 0, drawnLinks: 0, drawnLabels: 0 };
    if (scene.activeNodes > 0) this.lastStats = this.paint(ctx, w, h, scene);
    this.frameEma = this.frameEma * 0.9 + (performance.now() - t0) * 0.1;
  }

  private paint(ctx: CanvasRenderingContext2D, w: number, h: number, scene: RenderScene): RenderStats {
    const { data, disp, enter, degree, palette, display, highlight, linkA, linkB } = scene;
    const cam = scene.camera;
    const n = scene.activeNodes;
    const dimStrength = Math.min(1, Math.max(0, scene.dimStrength));
    const dimming = dimStrength > 0.02;
    /** What the dimmed things fade TO, interpolated by the eased strength. */
    const dimAlpha = 1 - dimStrength * (1 - palette.dimAlpha);
    const flat = cam.z < FLAT_Z;
    const rz = radiusZoom(cam.z);

    // ---- 1. project and cull
    let drawnNodes = 0;
    for (let i = 0; i < n; i++) {
      const x = toScreenX(disp[i * 2]!, cam, w);
      const y = toScreenY(disp[i * 2 + 1]!, cam, h);
      this.sx[i] = x;
      this.sy[i] = y;
      const grow = enter[i]! >= 1 ? 1 : easeOutBack(Math.max(0, enter[i]!));
      const rr = nodeRadius(degree[i] ?? 0, display.nodeSizeMultiplier) * rz * grow;
      this.r[i] = Math.min(MAX_R_PX, Math.max(MIN_R_PX, rr));
      const vis = x >= -CULL_MARGIN && x <= w + CULL_MARGIN && y >= -CULL_MARGIN && y <= h + CULL_MARGIN ? 1 : 0;
      this.visible[i] = vis;
      if (vis) drawnNodes++;
    }

    // ---- 2. links, bucketed by style
    const lineW = Math.max(0.4, display.lineSizeMultiplier * (flat ? 0.6 : 1));
    const budgetExceeded = scene.activeLinks > MAX_DRAWN_LINKS;
    let degFloor = 0;
    if (budgetExceeded) {
      // shed leaf-to-leaf fuzz first: it carries the least structure
      degFloor = scene.activeLinks > MAX_DRAWN_LINKS * 2 ? 3 : 2;
    }
    if (this.frameEma > 34) degFloor = Math.max(degFloor, 3);

    let drawnLinks = 0;
    /** Links with an endpoint mid-entrance; few, drawn solo so they fade in. */
    const enteringLinks: number[] = [];
    for (let pass = 0; pass < 3; pass++) {
      if (flat && pass !== L_NORMAL) continue;
      ctx.beginPath();
      let any = false;
      for (let j = 0; j < scene.activeLinks; j++) {
        const a = linkA[j]!;
        const b = linkB[j]!;
        if (a < 0 || b < 0 || a >= n || b >= n) continue;
        if (degFloor > 0 && Math.max(degree[a] ?? 0, degree[b] ?? 0) < degFloor) continue;
        if (!this.visible[a] && !this.visible[b]) continue;
        let bucket = L_NORMAL;
        if (dimming) bucket = highlight[a] && highlight[b] ? L_HIGH : L_DIM;
        if (flat) bucket = L_NORMAL;
        if (bucket !== pass) continue;
        const ea = Math.min(enter[a]!, enter[b]!);
        if (ea <= 0) continue;
        if (ea < 1) {
          if (pass === L_NORMAL) enteringLinks.push(j);
          continue;
        }
        ctx.moveTo(this.sx[a]!, this.sy[a]!);
        ctx.lineTo(this.sx[b]!, this.sy[b]!);
        any = true;
        if (pass === L_NORMAL || pass === L_HIGH) drawnLinks++;
      }
      if (!any) continue;
      ctx.lineWidth = lineW;
      if (pass === L_HIGH) {
        // the path survives until the next beginPath, so stroke it twice:
        // base link color, then the accent washed in by the eased strength
        ctx.globalAlpha = 1;
        ctx.strokeStyle = palette.link;
        ctx.stroke();
        ctx.globalAlpha = dimStrength;
        ctx.strokeStyle = palette.linkHighlight;
        ctx.stroke();
      } else {
        ctx.globalAlpha = pass === L_DIM ? dimAlpha : 1;
        ctx.strokeStyle = palette.link;
        ctx.stroke();
      }
    }
    // a link fades in WITH its arriving endpoint instead of popping at full
    // strength one frame after the node lands
    if (enteringLinks.length) {
      ctx.lineWidth = lineW;
      ctx.strokeStyle = palette.link;
      for (const j of enteringLinks) {
        const a = linkA[j]!;
        const b = linkB[j]!;
        const ea = Math.min(enter[a]!, enter[b]!);
        ctx.globalAlpha = easeOutCubic(ea);
        ctx.beginPath();
        ctx.moveTo(this.sx[a]!, this.sy[a]!);
        ctx.lineTo(this.sx[b]!, this.sy[b]!);
        ctx.stroke();
        drawnLinks++;
      }
    }
    ctx.globalAlpha = 1;

    // ---- 3. arrowheads
    if (display.showArrows && cam.z >= ARROW_MIN_Z && this.frameEma < 26) {
      ctx.beginPath();
      let any = false;
      for (let j = 0; j < scene.activeLinks; j++) {
        const a = linkA[j]!;
        const b = linkB[j]!;
        if (a < 0 || b < 0 || a >= n || b >= n) continue;
        if (!this.visible[b]) continue;
        if (enter[a]! <= 0 || enter[b]! <= 0) continue;
        const dx = this.sx[b]! - this.sx[a]!;
        const dy = this.sy[b]! - this.sy[a]!;
        const len = Math.hypot(dx, dy);
        if (len < 1) continue;
        const ux = dx / len;
        const uy = dy / len;
        // sit on the target's rim, not its centre
        const tipX = this.sx[b]! - ux * this.r[b]!;
        const tipY = this.sy[b]! - uy * this.r[b]!;
        const baseX = tipX - ux * ARROW_LEN;
        const baseY = tipY - uy * ARROW_LEN;
        ctx.moveTo(tipX, tipY);
        ctx.lineTo(baseX - uy * ARROW_WIDE, baseY + ux * ARROW_WIDE);
        ctx.lineTo(baseX + uy * ARROW_WIDE, baseY - ux * ARROW_WIDE);
        any = true;
      }
      if (any) {
        ctx.fillStyle = palette.arrow;
        ctx.globalAlpha = 1 - dimStrength * 0.5;
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    }

    // ---- 4. nodes, bucketed by fill colour
    for (let k = 0; k < this.bucketLen.length; k++) this.bucketLen[k] = 0;
    const entering: number[] = [];
    for (let i = 0; i < n; i++) {
      if (!this.visible[i]) continue;
      if (i === scene.currentIndex) continue;
      if (enter[i]! < 1) {
        if (enter[i]! > 0) entering.push(i);
        continue;
      }
      const c = this.colorId[i]!;
      const bucket = this.buckets[c];
      if (!bucket) continue;
      bucket[this.bucketLen[c]!] = i;
      this.bucketLen[c] = this.bucketLen[c]! + 1;
    }
    for (let c = 0; c < this.colors.length; c++) {
      const count = this.bucketLen[c]!;
      if (!count) continue;
      const bucket = this.buckets[c]!;
      ctx.beginPath();
      for (let k = 0; k < count; k++) {
        const i = bucket[k]!;
        // moveTo before every arc, or consecutive arcs are joined by a stray
        // line and the graph grows spider webs
        ctx.moveTo(this.sx[i]! + this.r[i]!, this.sy[i]!);
        ctx.arc(this.sx[i]!, this.sy[i]!, this.r[i]!, 0, TAU);
      }
      ctx.globalAlpha = dimming ? dimAlpha : 1;
      ctx.fillStyle = this.colors[c]!;
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    // highlighted nodes redrawn at full alpha over the dimmed pass
    if (dimming) {
      for (let c = 0; c < this.colors.length; c++) {
        const count = this.bucketLen[c]!;
        if (!count) continue;
        const bucket = this.buckets[c]!;
        ctx.beginPath();
        let any = false;
        for (let k = 0; k < count; k++) {
          const i = bucket[k]!;
          if (!highlight[i]) continue;
          ctx.moveTo(this.sx[i]! + this.r[i]!, this.sy[i]!);
          ctx.arc(this.sx[i]!, this.sy[i]!, this.r[i]!, 0, TAU);
          any = true;
        }
        if (!any) continue;
        ctx.fillStyle = this.colors[c]!;
        ctx.fill();
      }
    }

    // ---- 5. entering nodes, individually (per-node alpha breaks batching)
    for (const i of entering) {
      const t = enter[i]!;
      ctx.globalAlpha = easeOutCubic(Math.min(1, t / 0.6));
      ctx.beginPath();
      ctx.arc(this.sx[i]!, this.sy[i]!, this.r[i]!, 0, TAU);
      ctx.fillStyle = this.colors[this.colorId[i]!]!;
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // ---- 6. the currently open note
    const cur = scene.currentIndex;
    if (cur >= 0 && cur < n && this.visible[cur]) {
      ctx.beginPath();
      ctx.arc(this.sx[cur]!, this.sy[cur]!, this.r[cur]!, 0, TAU);
      ctx.fillStyle = palette.current;
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = palette.currentRing;
      ctx.stroke();
    }

    // ---- 7. hovered node ring
    if (scene.hoverIndex >= 0 && scene.hoverIndex < n) {
      const i = scene.hoverIndex;
      ctx.beginPath();
      ctx.arc(this.sx[i]!, this.sy[i]!, this.r[i]! + 2, 0, TAU);
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = palette.hover;
      ctx.stroke();
    }

    // ---- 8. labels
    let drawnLabels = 0;
    const baseAlpha = labelOpacity(cam.z, display.textFadeMultiplier);
    const labelsAllowed = this.frameEma < 20 && cam.z >= FLAT_Z;
    if ((baseAlpha > LABEL_ALPHA_FLOOR || scene.hoverIndex >= 0) && labelsAllowed) {
      ctx.font = `${LABEL_PX}px var(--font-ui, system-ui), system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.lineJoin = "round";
      for (let k = 0; k < this.labelOrder.length && drawnLabels < MAX_LABELS; k++) {
        const i = this.labelOrder[k]!;
        if (i >= n || !this.visible[i]) continue;
        const isHovered = i === scene.hoverIndex || (dimming && highlight[i] === 1);
        let alpha = baseAlpha * enter[i]!;
        if (dimming) alpha *= isHovered ? 1 : dimAlpha;
        // the hovered node keeps its label whatever the zoom
        if (i === scene.hoverIndex) alpha = 1;
        if (alpha <= LABEL_ALPHA_FLOOR) continue;
        const node = data.nodes[i]!;
        const text = truncateLabel(node.label);
        ctx.globalAlpha = alpha;
        // strokeText halo, NEVER shadowBlur: shadowBlur is 10 to 50x slower
        // and this runs per label per frame
        ctx.lineWidth = 3;
        ctx.strokeStyle = palette.labelHalo;
        ctx.strokeText(text, this.sx[i]!, this.sy[i]! + this.r[i]! + 3);
        ctx.fillStyle = i === scene.hoverIndex ? palette.hover : palette.label;
        ctx.fillText(text, this.sx[i]!, this.sy[i]! + this.r[i]! + 3);
        drawnLabels++;
      }
      ctx.globalAlpha = 1;
    }

    return { drawnNodes, drawnLinks, drawnLabels };
  }

  /** Topmost node under a screen point, or -1. */
  hitTest(scene: RenderScene, px: number, py: number, slop = 4): number {
    const n = scene.activeNodes;
    // reverse draw order so the node visually on top wins. A quadtree would
    // have to be rebuilt every tick because positions never stop moving, and
    // 2000 distance checks throttled to rAF is about 120k ops/sec.
    for (let i = n - 1; i >= 0; i--) {
      if (!this.visible[i]) continue;
      const dx = px - this.sx[i]!;
      const dy = py - this.sy[i]!;
      const rr = this.r[i]! + slop;
      if (dx * dx + dy * dy <= rr * rr) return i;
    }
    return -1;
  }
}
